import { HTMLAttributes, useCallback, useEffect, useId, useLayoutEffect, useRef } from "react"
import { inputVariant } from "@/components/ui/input"
import { cn } from "@/lib/utils"

interface ChatTextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  assistiveText?: string | React.ReactNode
  error?: string
  translateError?: boolean
  wrapperProps?: React.HTMLAttributes<HTMLDivElement>
  errorClasses?: HTMLAttributes<HTMLParagraphElement>["className"]
  "data-testid"?: string
  ref?: React.Ref<HTMLTextAreaElement>
}

export const ChatTextarea = (props: ChatTextareaProps) => {
  const { className, error, wrapperProps, "data-testid": testId, onChange, value, ref: forwardedRef, ...rest } = props
  const id = useId()
  const internalRef = useRef<HTMLTextAreaElement | null>(null)
  const wrapperElementRef = useRef<HTMLDivElement | null>(null)
  const previousWrapperWidthRef = useRef<number | null>(null)

  const resizeHeight = useCallback(() => {
    const current = internalRef?.current
    if (current) {
      current.style.height = "auto"
      const { scrollHeight, offsetHeight, clientHeight } = current
      const yBorderWidth = offsetHeight - clientHeight
      current.style.height = `${scrollHeight + yBorderWidth}px`
    }
  }, [internalRef])

  useLayoutEffect(() => {
    resizeHeight()
  }, [value, resizeHeight])

  useEffect(() => {
    const wrapper = wrapperElementRef.current
    if (!wrapper) return

    const resizeObserver = new ResizeObserver((entries) => {
      const wrapperWidth = entries[0]?.contentRect.width ?? 0
      if (wrapperWidth === previousWrapperWidthRef.current) return
      previousWrapperWidthRef.current = wrapperWidth
      resizeHeight()
    })
    resizeObserver.observe(wrapper)

    return () => resizeObserver.disconnect()
  }, [resizeHeight])

  return (
    <div ref={wrapperElementRef} className={cn(wrapperProps?.className, "flex w-full justify-center")}>
      <textarea
        autoComplete="off"
        aria-autocomplete="none"
        placeholder=" "
        data-testid={`${testId}-input`}
        rows={1}
        id={id}
        className={cn(
          inputVariant({ type: "custom" }),
          "scrollbar-thumb-rounded-full scrollbar-w-1 hover:scrollbar-thumb-surface-pressed min-h-12! scrollbar-thin scrollbar-thumb-transparent scrollbar-track-transparent py-3.25 pr-3 pl-4",
          className,
          error && "outline-critical! outline-2",
        )}
        {...rest}
        value={value}
        ref={(e) => {
          if (forwardedRef) {
            if (typeof forwardedRef === "function") {
              forwardedRef(e)
            } else {
              forwardedRef.current = e
            }
          }
          internalRef.current = e
          resizeHeight()
        }}
        onChange={(e) => {
          onChange?.(e)
          resizeHeight()
        }}
      />
    </div>
  )
}
